import { Controller } from "@hotwired/stimulus"

// The list of questions on a topic's page at /interview-questions/<topic>.
//
// Every question is a page of its own and the list is plain links, so this
// does two small things and nothing a reader needs in order to read:
//
// - **Filters.** Difficulty and type hide cards in the list. The choice is
//   kept for the topic while the tab is open, so coming back from a question
//   does not throw it away. It lives in sessionStorage, a convenience for
//   this reader alone, and the page is whole without it.
// - **Old links.** A question used to be "#its-slug" on its topic's page. Such
//   an address goes on to the question's own page.
export default class extends Controller {
  static targets = ["list", "entry", "filter", "controls", "badge", "empty"]
  static values = { topic: String }

  connect() {
    if (this.forwardOldLink()) return

    this.controlsTargets.forEach((controls) => (controls.hidden = false))
    this.restoreFilters()
  }

  filter() {
    const chosen = this.chosen()
    let shown = 0
    this.entryTargets.forEach((entry) => {
      const visible = Object.entries(chosen).every(([key, value]) => entry.dataset[key] === value)
      entry.hidden = !visible
      if (visible) shown += 1
    })
    if (this.hasEmptyTarget) this.emptyTarget.hidden = shown > 0

    const count = Object.keys(chosen).length
    this.badgeTargets.forEach((badge) => {
      badge.textContent = count
      badge.hidden = count === 0
    })
    this.store(chosen)
  }

  clearFilters() {
    this.filterTargets.forEach((input) => (input.checked = input.value === ""))
    this.filter()
  }

  chosen() {
    const chosen = {}
    this.filterTargets.forEach((input) => {
      if (input.checked && input.value) chosen[input.dataset.filterKey] = input.value
    })
    return chosen
  }

  restoreFilters() {
    const saved = this.saved()
    if (!saved || !this.hasFilterTarget) return

    this.filterTargets.forEach((input) => {
      input.checked = (saved[input.dataset.filterKey] || "") === input.value
    })
    this.filter()
  }

  forwardOldLink() {
    const slug = window.location.hash.slice(1)
    if (!slug || !this.hasListTarget) return false

    const item = this.listTarget.querySelector(`[data-slug="${CSS.escape(slug)}"]`)
    if (!item) return false

    window.location.replace(item.href)
    return true
  }

  get storageKey() {
    return `question-filters:${this.topicValue}`
  }

  saved() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.storageKey) || "null")
    } catch {
      return null
    }
  }

  store(chosen) {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(chosen))
    } catch {
      // Storage refused, in a private window or with site data blocked: the
      // filters still work on this page, they are just not carried to the next.
    }
  }
}
